import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { API_BASE } from '../config';
import { PanelContainer, Title, Subtitle, PrimaryButton, ToolbarButton, PageHeader, TitleRow, IconButton } from '../ui/common';
import { ArrowLeftIcon } from '../ui/icons';
import { Banner } from '../components/ui/Banner';
import { useTranslation } from '../translations';
import { formatDate } from '../utils/format';
import SimpleSelect from '../ui/SimpleSelect';
import { czytajPlikKsef } from '../utils/ksefPliki';
import { stanTokenow } from '../utils/ksefTokeny';
import { stanCertyfikatu } from '../utils/ksefUwaga';
import { KsefLogowanieBaner } from '../components/invoices/KsefUwaga';

// P4.2 (KSeF, ADR-0002): konfiguracja wysyłki faktur ustrukturyzowanych per organizacja.
// Token KSeF generuje się w Aplikacji Podatnika MF i wkleja tutaj (jak klucze P24), a certyfikat
// KSeF wgrywa jako pliki .crt i .key z hasłem (F.17); backend przechowuje je zaszyfrowane
// (AES-256-GCM) i nigdy nie zwraca na front.

// Oficjalna strona MF o certyfikatach KSeF (kto, typy, ważność), link z instrukcji.
const MF_CERTYFIKATY_URL = 'https://ksef.podatki.gov.pl/informacje-ogolne-ksef-20/certyfikaty-ksef/';


const Card = styled.div`
  background: var(--card-bg);
  border: 1px solid var(--input-border);
  border-radius: var(--r-lg);
  box-shadow: var(--shadow-md);
  padding: var(--sp-5) var(--sp-6);
  margin-bottom: var(--sp-5);
`;
const CardTitle = styled.h3`font-size: var(--fs-md); margin: 0 0 var(--sp-3); color: var(--text);`;
const P = styled.p`margin: 0 0 var(--sp-3); color: var(--text); font-size: var(--fs-base); line-height: 1.55;`;
const Hint = styled.p`margin: var(--sp-2) 0 0; font-size: var(--fs-sm); color: var(--text-muted);`;
const Row = styled.div`display: flex; align-items: center; gap: var(--sp-3); flex-wrap: wrap; margin-top: var(--sp-4);`;
const Field = styled.label`
  display: flex; flex-direction: column; gap: 6px;
  font-size: var(--fs-sm); font-weight: 600; color: var(--text-muted);
`;
const Input = styled.input`
  width: 320px; max-width: 100%;
  padding: 9px var(--sp-3);
  border: 1px solid var(--input-border); border-radius: var(--r-sm);
  background: var(--input-bg); color: var(--text); font-size: var(--fs-base);
`;
const Badge = styled.span`
  display: inline-flex; align-items: center; gap: 7px;
  padding: 5px var(--sp-3); border-radius: var(--r-pill); font-size: var(--fs-sm); font-weight: 600;
  background: ${p => (p.$ok ? 'var(--success-bg)' : 'var(--warning-bg)')};
  color: ${p => (p.$ok ? 'var(--success)' : 'var(--warning)')};
  border: 1px solid ${p => (p.$ok ? 'var(--success-border)' : 'var(--warning-border)')};
`;
const Steps = styled.ol`
  margin: 0 0 4px; padding-left: 20px; color: var(--text); font-size: var(--fs-md); line-height: 1.7;
`;
const FileInput = styled.input`
  font-size: var(--fs-sm); color: var(--text); max-width: 100%;
`;
const FileNote = styled.span`font-weight: 400; color: var(--text-muted); font-size: var(--fs-sm);`;
const Guide = styled.details`
  summary { cursor: pointer; font-size: var(--fs-md); font-weight: 600; color: var(--text); }
  h4 { margin: var(--sp-4) 0 var(--sp-2); font-size: var(--fs-base); color: var(--text); }
  ul { margin: 0; padding-left: 20px; color: var(--text); line-height: 1.6; }
  a { color: var(--primary); }
`;
const PasteDetails = styled.details`
  margin-top: var(--sp-3);
  summary { cursor: pointer; color: var(--text-muted); font-size: var(--fs-sm); }
`;
const PemArea = styled.textarea`
  width: 420px; max-width: 100%; min-height: 96px; resize: vertical;
  padding: 9px var(--sp-3);
  border: 1px solid var(--input-border); border-radius: var(--r-sm);
  background: var(--input-bg); color: var(--text);
  font-family: monospace; font-size: var(--fs-xs);
`;

function KsefSettings() {
  const { t, currentLanguage } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [banner, setBanner] = useState(null);
  const [enabled, setEnabled] = useState(false);
  const [environment, setEnvironment] = useState('test');
  const [hasToken, setHasToken] = useState(false);
  const [token, setToken] = useState('');
  const [autoSend, setAutoSend] = useState(false);
  // P4.4: uwierzytelnianie certyfikatem KSeF (XAdES), cert i klucz write-only jak token.
  const [authMethod, setAuthMethod] = useState('token');
  const [hasCert, setHasCert] = useState(false);
  const [certValidTo, setCertValidTo] = useState(null);
  const [certSubject, setCertSubject] = useState(null);
  const [certPem, setCertPem] = useState('');
  const [certKey, setCertKey] = useState('');
  // F.17: pliki z Aplikacji Podatnika KSeF (.crt/.cer i .key) oraz hasło do klucza. Hasło idzie
  // tylko w zapisie ustawień, serwer odszyfrowuje nim klucz i go nie zapisuje.
  const [certFileName, setCertFileName] = useState('');
  const [keyFileName, setKeyFileName] = useState('');
  const [keyPassword, setKeyPassword] = useState('');
  const [fileInputsKey, setFileInputsKey] = useState(0);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [tokenSunset, setTokenSunset] = useState(null);
  const [prodSince, setProdSince] = useState(null);
  // F.20: maile do administratorów o fakturach poza KSeF i o certyfikacie (domyślnie włączone).
  const [alertEmail, setAlertEmail] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/ksef-settings`, { credentials: 'include' });
      const data = await res.json();
      setTokenSunset(data.tokenSunset || null);
      if (data.settings) {
        setEnabled(!!data.settings.enabled);
        setEnvironment(data.settings.environment || 'test');
        setHasToken(!!data.settings.has_token);
        setAutoSend(!!data.settings.auto_send);
        setAuthMethod(data.settings.auth_method === 'certificate' ? 'certificate' : 'token');
        setHasCert(!!data.settings.has_cert);
        setCertValidTo(data.settings.cert_valid_to || null);
        setCertSubject(data.settings.cert_subject || null);
        setProdSince(data.settings.prod_since || null);
        setAlertEmail(data.settings.alert_email !== false);
      }
    } catch (e) {
      setBanner({ ok: false, msg: t('ksefSettings.loadError') });
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    setBanner(null);
    try {
      const csrf = localStorage.getItem('csrfToken') || '';
      const res = await fetch(`${API_BASE}/api/ksef-settings`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'x-csrf-token': csrf },
        body: JSON.stringify({
          enabled, environment, ksef_token: token, auto_send: autoSend, alert_email: alertEmail,
          auth_method: authMethod, ksef_cert_pem: certPem, ksef_cert_key: certKey,
          ...(certKey ? { ksef_cert_key_password: keyPassword } : {}),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || t('ksefSettings.saveError'));
      setToken(''); // sekrety nie zostają w formularzu po zapisie
      setCertPem('');
      setCertKey('');
      setKeyPassword('');
      setCertFileName('');
      setKeyFileName('');
      setFileInputsKey((k) => k + 1);
      setTestResult(null);
      setBanner({ ok: true, msg: t('ksefSettings.saved') });
      setLoading(true);
      await load();
    } catch (e) {
      setBanner({ ok: false, msg: e.message || t('ksefSettings.saveError') });
    } finally {
      setSaving(false);
    }
  };

  const wybierzPlik = (ustawTresc, ustawNazwe) => async (e) => {
    const plik = e.target.files && e.target.files[0];
    if (!plik) return;
    setBanner(null);
    try {
      ustawTresc(await czytajPlikKsef(plik));
      ustawNazwe(plik.name);
    } catch (err) {
      ustawTresc('');
      ustawNazwe('');
      setBanner({ ok: false, msg: t(err && err.zaDuzy ? 'ksefSettings.fileTooLarge' : 'ksefSettings.fileReadError', { name: plik.name }) });
    }
  };

  const testConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const csrf = localStorage.getItem('csrfToken') || '';
      const res = await fetch(`${API_BASE}/api/ksef-settings/test-connection`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'x-csrf-token': csrf },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) throw new Error(data.error || t('ksefSettings.testError'));
      const env = { test: t('ksefSettings.envTestShort'), demo: t('ksefSettings.envDemoShort'), prod: t('ksefSettings.envProdShort') }[data.environment] || data.environment;
      const method = data.method === 'certificate' ? t('ksefSettings.testMethodCert') : t('ksefSettings.testMethodToken');
      setTestResult({ ok: true, msg: t('ksefSettings.testOk', { method, env }) });
    } catch (e) {
      setTestResult({ ok: false, msg: e.message || t('ksefSettings.testError') });
    } finally {
      setTesting(false);
    }
  };

  const tokeny = stanTokenow(tokenSunset);
  // F.19: zapisany certyfikat kończący się w ciągu 30 dni albo już nieważny (progi jak na serwerze).
  const certyfikat = authMethod === 'certificate' && hasCert ? stanCertyfikatu(certValidTo) : null;

  if (loading) return <PanelContainer><Subtitle>{t('ksefSettings.loading')}</Subtitle></PanelContainer>;

  return (
    <PanelContainer>
      {/* Trasa `/ksef-settings` nie jest w menu bocznym, wiec wchodzi sie tu tylko z Profilu. */}
      <PageHeader>
        <TitleRow>
          <IconButton onClick={() => navigate('/profile')} aria-label={t('common.backToProfile')}>
            <ArrowLeftIcon />
          </IconButton>
          <div>
            <Title>{t('ksefSettings.title')}</Title>
            <Subtitle>{t('ksefSettings.subtitle')}</Subtitle>
          </div>
        </TitleRow>
      </PageHeader>

      <Card>
        <CardTitle>{t('ksefSettings.configCardTitle')}</CardTitle>
        <P>{t('ksefSettings.authIntro')}</P>
        <Steps>
          <li>{t('ksefSettings.step1')}</li>
          <li>{t('ksefSettings.step2')}</li>
          <li>{t('ksefSettings.step3')}</li>
        </Steps>
        {tokeny.stan === 'brak' ? (
          <Hint data-cy="ksef-token-info">{t('ksefSettings.tokenFutureHint')}</Hint>
        ) : (
          <div style={{ marginTop: 'var(--sp-2)' }} data-cy="ksef-token-sunset">
            <Banner tone="warning" compact>
              {tokeny.stan === 'po'
                ? t('ksefSettings.tokenSunsetPassed', { date: tokeny.data })
                : t('ksefSettings.tokenSunsetHint', { date: tokeny.data })}
            </Banner>
          </div>
        )}

        <Row>
          {authMethod === 'certificate' ? (
            <Badge $ok={hasCert}>
              {hasCert
                ? (certValidTo ? t('ksefSettings.certSavedWithDate', { date: formatDate(certValidTo, { locale: currentLanguage }) }) : t('ksefSettings.certSaved'))
                : t('ksefSettings.noCert')}
            </Badge>
          ) : (
            <Badge $ok={hasToken}>{hasToken ? t('ksefSettings.tokenSaved') : t('ksefSettings.noToken')}</Badge>
          )}
          <Badge $ok={enabled}>{enabled ? t('ksefSettings.sendingEnabled') : t('ksefSettings.sendingDisabled')}</Badge>
        </Row>
        {authMethod === 'certificate' && hasCert && certSubject && (
          <Hint>{t('ksefSettings.certSubjectLabel', { subject: certSubject })}</Hint>
        )}
        {certyfikat && (
          <div style={{ marginTop: 'var(--sp-3)' }}>
            <KsefLogowanieBaner auth={certyfikat} bezPrzycisku />
          </div>
        )}
        {enabled && environment === 'prod' && prodSince && (
          <Hint data-cy="ksef-prod-since">{t('ksefSettings.prodSince', { date: formatDate(prodSince, { locale: currentLanguage }) })}</Hint>
        )}

        <Row>
          <Field as="div">
            {t('ksefSettings.environment')}
            <SimpleSelect
              style={{ width: 220 }}
              value={environment}
              onChange={setEnvironment}
              options={[
                { value: 'test', label: t('ksefSettings.envTest') },
                { value: 'demo', label: t('ksefSettings.envDemo') },
                { value: 'prod', label: t('ksefSettings.envProd') },
              ]}
            />
          </Field>
          <Field as="div">
            {t('ksefSettings.authMethod')}
            <SimpleSelect
              style={{ width: 220 }}
              value={authMethod}
              onChange={setAuthMethod}
              options={[
                { value: 'token', label: t('ksefSettings.authMethodToken') },
                { value: 'certificate', label: t('ksefSettings.authMethodCert') },
              ]}
            />
          </Field>
        </Row>

        {authMethod === 'token' ? (
          <Row>
            <Field>
              {t('ksefSettings.tokenLabel')}
              <Input
                type="password"
                placeholder={hasToken ? t('ksefSettings.tokenPlaceholderSaved') : t('ksefSettings.tokenPlaceholderEmpty')}
                value={token}
                onChange={(e) => setToken(e.target.value)}
                autoComplete="off"
              />
            </Field>
          </Row>
        ) : (
          <>
            <Row key={fileInputsKey}>
              <Field>
                {t('ksefSettings.certFileLabel')}
                <FileInput
                  type="file"
                  accept=".crt,.cer,.pem,.der"
                  data-cy="ksef-cert-file"
                  onChange={wybierzPlik(setCertPem, setCertFileName)}
                />
                <FileNote>
                  {certFileName ? t('ksefSettings.fileChosen', { name: certFileName }) : (hasCert ? t('ksefSettings.fileSavedHint') : '')}
                </FileNote>
              </Field>
              <Field>
                {t('ksefSettings.keyFileLabel')}
                <FileInput
                  type="file"
                  accept=".key,.pem,.der"
                  data-cy="ksef-key-file"
                  onChange={wybierzPlik(setCertKey, setKeyFileName)}
                />
                <FileNote>
                  {keyFileName ? t('ksefSettings.fileChosen', { name: keyFileName }) : (hasCert ? t('ksefSettings.fileSavedHint') : '')}
                </FileNote>
              </Field>
              <Field>
                {t('ksefSettings.keyPasswordLabel')}
                <Input
                  type="password"
                  data-cy="ksef-key-password"
                  value={keyPassword}
                  onChange={(e) => setKeyPassword(e.target.value)}
                  autoComplete="new-password"
                />
              </Field>
            </Row>
            <Hint>{t('ksefSettings.keyPasswordHint')}</Hint>
            <PasteDetails>
              <summary>{t('ksefSettings.pasteToggle')}</summary>
              <Row>
                <Field>
                  {t('ksefSettings.certPemLabel')}
                  <PemArea
                    placeholder={hasCert ? t('ksefSettings.pemPlaceholderSaved') : '-----BEGIN CERTIFICATE-----'}
                    value={certPem}
                    onChange={(e) => { setCertPem(e.target.value); setCertFileName(''); }}
                    autoComplete="off"
                    spellCheck={false}
                  />
                </Field>
                <Field>
                  {t('ksefSettings.certKeyLabel')}
                  <PemArea
                    placeholder={hasCert ? t('ksefSettings.pemPlaceholderSaved') : '-----BEGIN ENCRYPTED PRIVATE KEY-----'}
                    value={certKey}
                    onChange={(e) => { setCertKey(e.target.value); setKeyFileName(''); }}
                    autoComplete="off"
                    spellCheck={false}
                  />
                </Field>
              </Row>
            </PasteDetails>
          </>
        )}
        <Row>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text)', fontSize: 'var(--fs-base)', cursor: 'pointer' }}>
            <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
            {t('ksefSettings.enableSending')}
          </label>
        </Row>
        <Row>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text)', fontSize: 'var(--fs-base)', cursor: 'pointer' }}>
            <input type="checkbox" checked={autoSend} onChange={(e) => setAutoSend(e.target.checked)} />
            {t('ksefSettings.autoSendLabel')}
          </label>
        </Row>
        <Hint>{t('ksefSettings.autoSendHint')}</Hint>
        <Row>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text)', fontSize: 'var(--fs-base)', cursor: 'pointer' }}>
            <input type="checkbox" data-cy="ksef-alert-email" checked={alertEmail} onChange={(e) => setAlertEmail(e.target.checked)} />
            {t('ksefSettings.alertEmailLabel')}
          </label>
        </Row>
        <Hint>{t('ksefSettings.alertEmailHint')}</Hint>
        <Row>
          <PrimaryButton onClick={save} disabled={saving} data-cy="ksef-save">{saving ? t('ksefSettings.saving') : t('ksefSettings.saveSettings')}</PrimaryButton>
        </Row>

        {banner && (
          <div style={{ marginTop: 'var(--sp-3)' }}>
            <Banner tone={banner.ok ? 'success' : 'danger'} compact>{banner.msg}</Banner>
          </div>
        )}

        <Row>
          <ToolbarButton type="button" onClick={testConnection} disabled={testing} data-cy="ksef-test-connection">
            {testing ? t('ksefSettings.testing') : t('ksefSettings.testConnection')}
          </ToolbarButton>
        </Row>
        <Hint>{t('ksefSettings.testHint')}</Hint>
        {testResult && (
          <div style={{ marginTop: 'var(--sp-3)' }} data-cy="ksef-test-result">
            <Banner tone={testResult.ok ? 'success' : 'danger'} compact>{testResult.msg}</Banner>
          </div>
        )}
      </Card>

      <Card>
        <Guide data-cy="ksef-cert-guide">
          <summary>{t('ksefSettings.certGuideTitle')}</summary>
          <P style={{ marginTop: 'var(--sp-3)' }}>{t('ksefSettings.certGuideIntro')}</P>
          <h4>{t('ksefSettings.certGuidePrepTitle')}</h4>
          <ul>
            <li>{t('ksefSettings.certGuidePrep1')}</li>
            <li>{t('ksefSettings.certGuidePrep2')}</li>
            <li>{t('ksefSettings.certGuidePrep3')}</li>
          </ul>
          <h4>{t('ksefSettings.certGuideStepsTitle')}</h4>
          <Steps>
            <li>{t('ksefSettings.certGuideStep1')}</li>
            <li>{t('ksefSettings.certGuideStep2')}</li>
            <li>{t('ksefSettings.certGuideStep3')}</li>
            <li>{t('ksefSettings.certGuideStep4')}</li>
            <li>{t('ksefSettings.certGuideStep5')}</li>
            <li>{t('ksefSettings.certGuideStep6')}</li>
            <li>{t('ksefSettings.certGuideStep7')}</li>
            <li>{t('ksefSettings.certGuideStep8')}</li>
            <li>{t('ksefSettings.certGuideStep9')}</li>
          </Steps>
          <h4>{t('ksefSettings.certGuideNotesTitle')}</h4>
          <ul>
            <li>{t('ksefSettings.certGuideNote1')}</li>
            <li>{t('ksefSettings.certGuideNote2')}</li>
            <li>{t('ksefSettings.certGuideNote3')}</li>
            <li>{t('ksefSettings.certGuideNote4')}</li>
            <li>{t('ksefSettings.certGuideNote5')}</li>
          </ul>
          <Hint>
            <a href={MF_CERTYFIKATY_URL} target="_blank" rel="noopener noreferrer">{t('ksefSettings.certGuideMfLink')}</a>
          </Hint>
        </Guide>
      </Card>

      <Card>
        <CardTitle>{t('ksefSettings.howItWorksTitle')}</CardTitle>
        <P>{t('ksefSettings.howItWorksBody')}</P>
      </Card>
    </PanelContainer>
  );
}

export default KsefSettings;
